import http from "../http-common";

/**
 * Downscale big phone photos before upload (max 1600px, JPEG) so the public
 * menu loads fast on diners' data plans. Falls back to the original file.
 */
const shrinkImage = (file, maxSide = 1600) => new Promise((resolve) => {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') { resolve(file); return; }
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    if (scale === 1 && file.size < 900 * 1024) { resolve(file); return; }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff'; // PNG transparency -> white, not black
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file);
    }, 'image/jpeg', 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const upload = async (url, file, maxSide) => {
  const formData = new FormData();
  formData.append('image', await shrinkImage(file, maxSide));
  return http.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

/**
 * Digital menu API (/api/menus/*). Public page: {public_url} (`/m/{slug}`).
 */
class MenuDataService {
  getMenus() {
    return http.get('/menus');
  }

  getMenu(uuid) {
    return http.get(`/menus/${uuid}`);
  }

  createMenu(data) {
    return http.post('/menus', data);
  }

  updateMenu(uuid, data) {
    return http.put(`/menus/${uuid}`, data);
  }

  deleteMenu(uuid) {
    return http.delete(`/menus/${uuid}`);
  }

  /** kind: logo | cover */
  uploadMenuImage(uuid, kind, file) {
    return upload(`/menus/${uuid}/images/${kind}`, file, kind === 'logo' ? 600 : 2000);
  }

  removeMenuImage(uuid, kind) {
    return http.delete(`/menus/${uuid}/images/${kind}`);
  }

  // Sections
  createSection(menuUuid, data) {
    return http.post(`/menus/${menuUuid}/sections`, data);
  }

  updateSection(menuUuid, uuid, data) {
    return http.put(`/menus/${menuUuid}/sections/${uuid}`, data);
  }

  deleteSection(menuUuid, uuid) {
    return http.delete(`/menus/${menuUuid}/sections/${uuid}`);
  }

  reorderSections(menuUuid, order) {
    return http.post(`/menus/${menuUuid}/sections/reorder`, { order });
  }

  // Dishes
  createItem(menuUuid, data) {
    return http.post(`/menus/${menuUuid}/items`, data);
  }

  updateItem(menuUuid, uuid, data) {
    return http.put(`/menus/${menuUuid}/items/${uuid}`, data);
  }

  deleteItem(menuUuid, uuid) {
    return http.delete(`/menus/${menuUuid}/items/${uuid}`);
  }

  reorderItems(menuUuid, section, order) {
    return http.post(`/menus/${menuUuid}/items/reorder`, { section, order });
  }

  uploadItemImage(menuUuid, uuid, file) {
    return upload(`/menus/${menuUuid}/items/${uuid}/image`, file, 1600);
  }

  removeItemImage(menuUuid, uuid) {
    return http.delete(`/menus/${menuUuid}/items/${uuid}/image`);
  }

  /** Orders sent from the public menu. params: { from, to (Y-m-d), status, page, per_page } */
  getOrders(menuUuid, params) {
    return http.get(`/menus/${menuUuid}/orders`, { params });
  }

  /** Counts by status and products sold (confirmed orders) for { from, to }. */
  getOrdersReport(menuUuid, params) {
    return http.get(`/menus/${menuUuid}/orders/report`, { params });
  }

  confirmOrder(menuUuid, uuid) {
    return http.post(`/menus/${menuUuid}/orders/${uuid}/confirm`);
  }

  cancelOrder(menuUuid, uuid) {
    return http.post(`/menus/${menuUuid}/orders/${uuid}/cancel`);
  }
}

export default new MenuDataService();
